import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bell, TrendingUp, AlertCircle, Loader2 } from 'lucide-react';
import api from '../../../utils/api';

/**
 * PushOpenRateWidget — pilot KPI card for the RetailerDashboard.
 *
 * Surfaces the retailer's push-notification delivery health from
 * ``/api/retailer/analytics/push-open-rate`` (backed by the
 * ``order_push_log`` collection).
 *
 * Metrics shown:
 *   - Total events fired (ready + picked_up) in the last 30 days
 *   - Delivery success rate (sent / total)
 *   - Device coverage (customers who had at least one push token)
 *   - Per-event breakdown (ready → DAC, picked_up → DRLP)
 *
 * Silent-fail: if the endpoint errors or the retailer has zero orders,
 * shows a friendly empty state instead of a stack trace.
 */
export default function PushOpenRateWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/retailer/analytics/push-open-rate', { params: { days: 30 } });
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || 'Could not load push metrics.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <Card data-testid="push-open-rate-widget">
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="w-4 h-4 text-emerald-600" />
            Push notification KPIs
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Last 30 days · &quot;Ready for pickup&quot; + &quot;Order picked up&quot; events
          </p>
        </div>
        {data && data.total_events > 0 && (
          <Badge
            className={
              data.delivery_success_rate >= 60 ? 'bg-emerald-500' :
              data.delivery_success_rate >= 30 ? 'bg-amber-500' : 'bg-slate-400'
            }
            data-testid="push-open-rate-badge"
          >
            {data.delivery_success_rate}% delivered
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading push metrics…
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4"
               data-testid="push-open-rate-error">
            <AlertCircle className="w-4 h-4 text-amber-500" /> {error}
          </div>
        )}

        {!loading && !error && data && data.total_events === 0 && (
          <div className="py-4 text-sm text-slate-500" data-testid="push-open-rate-empty">
            No push events yet. When a consumer opts in and you mark an order
            <span className="font-medium"> ready</span> or
            <span className="font-medium"> picked up</span>, metrics will appear here.
          </div>
        )}

        {!loading && !error && data && data.total_events > 0 && (
          <div className="space-y-4" data-testid="push-open-rate-body">
            <div className="grid grid-cols-3 gap-3">
              <StatBlock label="Events" value={data.total_events} tone="slate" testid="push-stat-total" />
              <StatBlock label="Delivered" value={data.sent} tone="emerald" testid="push-stat-sent" />
              <StatBlock label="No device" value={data.no_devices} tone="amber" testid="push-stat-nodevice" />
            </div>

            <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-3">
              <MiniStat
                label="Delivery success"
                value={`${data.delivery_success_rate}%`}
                hint="Sent to a live device"
                testid="push-stat-success-rate"
              />
              <MiniStat
                label="Device coverage"
                value={`${data.device_coverage_rate}%`}
                hint="Consumer had ≥1 push token"
                testid="push-stat-coverage"
              />
            </div>

            {Object.keys(data.by_event || {}).length > 0 && (
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> By event
                </p>
                <div className="space-y-1.5">
                  {Object.entries(data.by_event).map(([evt, counts]) => (
                    <EventRow key={evt} event={evt} counts={counts} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatBlock({ label, value, tone, testid }) {
  const toneMap = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
  };
  return (
    <div className="text-center" data-testid={testid}>
      <p className={`text-2xl font-bold ${toneMap[tone] || 'text-slate-900'}`}>{value}</p>
      <p className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function MiniStat({ label, value, hint, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline gap-2">
        <p className="text-xl font-bold text-slate-900">{value}</p>
      </div>
      <p className="text-xs font-medium text-slate-700">{label}</p>
      <p className="text-[11px] text-slate-400">{hint}</p>
    </div>
  );
}

function EventRow({ event, counts }) {
  const total = (counts.sent || 0) + (counts.no_devices || 0) + (counts.failed || 0);
  const label = event === 'ready' ? 'Order ready' :
                event === 'picked_up' ? 'Order picked up' : event;
  return (
    <div className="flex items-center justify-between text-xs" data-testid={`push-event-row-${event}`}>
      <span className="text-slate-600">{label}</span>
      <span className="text-slate-500 font-mono">
        <span className="text-emerald-600">{counts.sent || 0}</span>
        {' / '}
        <span>{total}</span>
      </span>
    </div>
  );
}
